import { useEffect, useState } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { X, Play, Wrench, Building2, Layers } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const mediaSrc = (url) => {
  if (!url) return url;
  if (url.startsWith("http") || url.startsWith("/media/")) return url;
  return `${process.env.REACT_APP_BACKEND_URL}${url}`;
};
const isVideoFile = (url) => /\.(mp4|webm|mov)(\?|$)/i.test(url || "");

export default function ProjectsGallery() {
  const [projects, setProjects] = useState([]);
  const [categories, setCategories] = useState(["All"]);
  const [filter, setFilter] = useState("All");
  const [active, setActive] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios
      .get(`${API}/projects`)
      .then((r) => setProjects(r.data))
      .catch(() => setProjects([]))
      .finally(() => setLoading(false));
    axios
      .get(`${API}/categories`)
      .then((r) => setCategories(["All", ...r.data.map((c) => c.name)]))
      .catch(() => {});
  }, []);

  const visible = filter === "All" ? projects : projects.filter((p) => p.category === filter);

  const coverOf = (p) => p.media?.find((m) => m.url === p.cover_url) || p.media?.[0];

  return (
    <section id="work" data-testid="work-gallery" className="py-20 lg:py-28">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-10">
          <div>
            <p className="font-mono2 text-xs tracking-[0.3em] uppercase text-[#FF4D23] mb-3">02 · Selected Work</p>
            <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight uppercase" data-testid="work-heading">
              Designs That <span className="text-[#FF4D23]">Perform</span>
            </h2>
          </div>
          <div className="flex gap-2 overflow-x-auto md:flex-wrap md:overflow-visible pb-2 md:pb-0 -mx-4 px-4 sm:mx-0 sm:px-0" data-testid="work-filters">
            {categories.map((f) => (
              <button
                key={f}
                data-testid={`work-filter-${f.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                onClick={() => setFilter(f)}
                className={`shrink-0 inline-flex items-center min-h-[44px] text-xs font-mono2 tracking-wider uppercase px-4 py-2 rounded-full border transition-colors ${
                  filter === f
                    ? "bg-[#FF4D23] border-[#FF4D23] text-[#0A0B0E] font-bold"
                    : "border-[#3B4254] text-[#B6BDCB] hover:border-[#FF4D23] hover:text-[#F5F6FA]"
                }`}
              >
                {f}
              </button>
            ))}
          </div>
        </div>

        {loading ? (
          <p className="text-[#8A92A3] font-mono2 text-sm" data-testid="work-loading">Loading work...</p>
        ) : visible.length === 0 ? (
          <p className="text-[#8A92A3] font-mono2 text-sm" data-testid="work-empty">No projects in this category yet.</p>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {visible.map((p, i) => {
              const cover = coverOf(p);
              return (
                <motion.button
                  key={p.id}
                  data-testid={`work-card-${i}`}
                  onClick={() => setActive(p)}
                  initial={{ opacity: 0, y: 24 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.45, delay: (i % 3) * 0.08 }}
                  className="group text-left bg-[#12141B] border border-[#222634] rounded-2xl overflow-hidden hover:border-[#3B4254] hover:-translate-y-1 transition-all duration-300"
                >
                  <div className="relative aspect-[4/3] overflow-hidden bg-[#0A0B0E]">
                    {cover?.type === "video" && isVideoFile(cover.url) ? (
                      <video
                        src={mediaSrc(cover.url)}
                        poster={cover.poster_url ? mediaSrc(cover.poster_url) : undefined}
                        preload="metadata"
                        className="w-full h-full object-cover"
                        muted
                      />
                    ) : cover ? (
                      <img
                        src={mediaSrc(cover.url)}
                        alt={p.title}
                        loading="lazy"
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-[#3B4254]">
                        <Layers size={32} />
                      </div>
                    )}
                    <span className="absolute top-3 left-3 text-[10px] font-mono2 tracking-widest uppercase bg-[#0A0B0E]/85 backdrop-blur border border-[#222634] text-[#B6BDCB] rounded-full px-3 py-1.5" data-testid={`work-tag-${i}`}>
                      {p.category}
                    </span>
                    {p.media?.length > 1 && (
                      <span className="absolute top-3 right-3 inline-flex items-center gap-1 text-[10px] font-mono2 font-bold bg-[#0A0B0E]/85 backdrop-blur border border-[#222634] text-[#F5F6FA] rounded-full px-3 py-1.5">
                        <Layers size={11} className="text-[#FF4D23]" /> {p.media.length}
                      </span>
                    )}
                    {p.metric_value && (
                      <span
                        className="absolute bottom-3 left-3 font-mono2 text-xs font-bold px-3 py-1.5 rounded-full bg-[#0A0B0E]/85 backdrop-blur border border-[#222634]"
                        style={{ color: p.metric_color }}
                        data-testid={`work-metric-${i}`}
                      >
                        {p.metric_value} {p.metric_label}
                      </span>
                    )}
                  </div>
                  <div className="p-5">
                    <h3 className="font-display font-bold text-lg group-hover:text-[#FF4D23] transition-colors">
                      {p.title}
                    </h3>
                    {p.brand && <p className="text-xs text-[#8A92A3] mt-1">{p.brand}</p>}
                  </div>
                </motion.button>
              );
            })}
          </div>
        )}
      </div>

      <AnimatePresence>
        {active && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[60] bg-[#0A0B0E]/90 backdrop-blur-md flex items-center justify-center p-4"
            onClick={() => setActive(null)}
            data-testid="work-modal"
          >
            <motion.div
              initial={{ opacity: 0, y: 32, scale: 0.97 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 32, scale: 0.97 }}
              onClick={(e) => e.stopPropagation()}
              data-lenis-prevent
              className="bg-[#12141B] border border-[#3B4254] rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto overscroll-contain"
            >
              <div className="p-6 sm:p-8 border-b border-[#222634] relative">
                <button
                  data-testid="work-modal-close"
                  onClick={() => setActive(null)}
                  className="absolute top-4 right-4 bg-[#0A0B0E]/80 backdrop-blur border border-[#3B4254] rounded-full p-2 hover:border-[#FF4D23] transition-colors"
                  aria-label="Close"
                >
                  <X size={18} />
                </button>
                <div className="flex flex-wrap items-center gap-3 mb-3">
                  <span className="text-[10px] font-mono2 tracking-widest uppercase text-[#FF4D23] border border-[#FF4D23]/40 rounded-full px-3 py-1" data-testid="work-modal-tag">
                    {active.category}
                  </span>
                  {active.brand && (
                    <span className="inline-flex items-center gap-1.5 text-xs text-[#B6BDCB]">
                      <Building2 size={13} /> {active.brand}
                    </span>
                  )}
                </div>
                <h3 className="font-display font-bold text-2xl pr-10" data-testid="work-modal-title">{active.title}</h3>
                {active.description && <p className="text-[#B6BDCB] text-sm leading-relaxed mt-3 max-w-2xl">{active.description}</p>}
                {active.metric_value && (
                  <div className="inline-flex items-center gap-4 border border-[#222634] rounded-xl px-5 py-3 mt-5" data-testid="work-modal-metric">
                    <span className="w-2.5 h-2.5 rounded-full" style={{ background: active.metric_color }} />
                    <span className="font-mono2 text-xl font-bold" style={{ color: active.metric_color }}>{active.metric_value}</span>
                    <span className="text-[10px] font-mono2 tracking-widest uppercase text-[#8A92A3]">{active.metric_label} — business impact</span>
                  </div>
                )}
                {active.tools?.length > 0 && (
                  <div className="flex flex-wrap items-center gap-2 mt-4">
                    <Wrench size={14} className="text-[#8A92A3]" />
                    {active.tools.map((t) => (
                      <span key={t} className="text-xs font-mono2 text-[#B6BDCB] border border-[#3B4254] rounded-full px-3 py-1">
                        {t}
                      </span>
                    ))}
                  </div>
                )}
              </div>
              <div className="p-6 sm:p-8">
                <div className="columns-1 sm:columns-2 gap-4" data-testid="work-modal-collage">
                  {(active.media || []).map((m, i) => (
                    <div key={i} className="mb-4 break-inside-avoid rounded-xl overflow-hidden border border-[#222634] bg-[#0A0B0E]">
                      {m.type === "video" && isVideoFile(m.url) ? (
                        <video
                          src={mediaSrc(m.url)}
                          poster={m.poster_url ? mediaSrc(m.poster_url) : undefined}
                          controls
                          muted
                          playsInline
                          preload="metadata"
                          className="w-full"
                        />
                      ) : (
                        <img src={mediaSrc(m.url)} alt={`${active.title} — ${active.category} design ${i + 1}`} className="w-full h-auto" />
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
}
